import { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import ResourceList from "src/components/shared/ResourceList";
import { plural } from "src/components/shared/format";
import { contentApi } from "src/pages/website-content/api";
import { faqOnSite, faqWritingWarnings, isOn, matches, placeholderWarnings, rights, shortName } from "src/pages/website-content/checks";
import {
  CheckCell,
  ConfirmDialog,
  ContentPage,
  EditorDialog,
  Field,
  HonestyCheck,
  SaveSwitch,
  ShowFilter,
  WhereLink,
  useConfirm,
  useContentList,
  useForm,
} from "src/pages/website-content/kit";
import { FaqPreview, SitePreview } from "src/pages/website-content/Preview";
import FaqNotes from "src/pages/website-content/FaqNotes";
import UndoToast from "src/components/shared/UndoToast";
import { canUndo } from "src/utils/undoDelete";

// General FAQs for the website's Questions page (/faqs, headed "What people
// ask"). An FAQ is on the website when its one "Live" switch is
// on (the API's is_main and is_active together) and it isn't placeholder
// text (see faqOnSite, which mirrors the website's own rule). A new FAQ
// starts off the website, so unreviewed words never go live by default.

const EMPTY = { id: null, questions: "", answers: "", is_main: false };

// Counted the way the website counts.
const onFaqPage = (item) => faqOnSite(item).shown;
const normal = (text) =>
  String(text || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

// What saving this FAQ changes on the website, said for this FAQ.
function editorNote(item) {
  if (!item) return "A new FAQ isn’t on the website until you switch it on.";
  const site = faqOnSite(item);
  if (site.shown) return "It is on the website: changes show on the Questions page as soon as you save.";
  if (!site.on) return "It isn’t on the website. Switch it on in the list when it’s ready.";
  return `${site.reason}. Replace the filler text and it shows on the Questions page.`;
}

const FAQ = () => {
  const dispatch = useDispatch();
  const can = rights("21");
  const load = useCallback(() => contentApi.faq.list(dispatch), [dispatch]);
  const list = useContentList(load);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("all");
  const [editing, setEditing] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const form = useForm(EMPTY);
  const confirm = useConfirm();

  const items = useMemo(() => list.items || [], [list.items]);
  const counts = { all: items.length, live: items.filter(onFaqPage).length };
  counts.other = counts.all - counts.live;
  const shown = items
    .filter((item) => (tab === "live" ? onFaqPage(item) : tab === "other" ? !onFaqPage(item) : true))
    .filter((item) => matches(item, search, ["questions", "answers"]))
    // What is on the website first.
    .sort((a, b) => Number(onFaqPage(b)) - Number(onFaqPage(a)));

  // The same question asked twice reads as a mistake on the website.
  const twins = useMemo(() => {
    const byText = {};
    items.forEach((item) => {
      const key = normal(item.questions);
      byText[key] = [...(byText[key] || []), item.id];
    });
    return byText;
  }, [items]);
  const warningsFor = (item) => {
    const found = [...placeholderWarnings(item.questions, item.answers), ...faqWritingWarnings(item.questions, item.answers)];
    const same = (twins[normal(item.questions)] || []).filter((id) => id !== item.id);
    if (same.length)
      found.push(same.length === 1 ? "Same question as 1 other in this list." : `Same question as ${same.length} others in this list.`);
    return found;
  };

  // The one switch: on puts it on the Questions page (and makes it active if
  // something switched it off before); off takes it off.
  const setOnWebsite = async (item, on) => {
    const name = shortName(item.questions, 60);
    const changes = on ? { is_main: 1, ...(Number(item.is_active) === 0 ? { is_active: 1 } : {}) } : { is_main: 0 };
    try {
      await contentApi.faq.edit({ id: item.id, ...changes }, dispatch, false);
      list.patch(item.id, changes);
      const next = faqOnSite({ ...item, ...changes });
      toast.success(
        on
          ? next.shown
            ? `“${name}” is on the website: the Questions page shows it.`
            : `“${name}” is switched on, but the website hides it: ${next.reason.toLowerCase()}.`
          : `“${name}” is off the website.`
      );
    } catch (err) {
      toast.error(`Couldn’t ${on ? "put" : "take"} “${name}” ${on ? "on" : "off"} the website. Nothing changed. Try again.`);
    }
  };

  const openEditor = (item) => {
    form.reset(item ? { id: item.id, questions: item.questions || "", answers: item.answers || "", is_main: isOn(item.is_main) } : EMPTY);
    setEditItem(item);
    setEditing(item ? "edit" : "add");
  };

  const save = async () => {
    const v = form.values;
    const errors = {};
    if (!v.questions.trim()) errors.questions = "Write the question as a visitor would ask it.";
    if (!v.answers.trim()) errors.answers = "Write the answer.";
    form.setErrors(errors);
    if (Object.keys(errors).length) return false;
    try {
      const body = { questions: v.questions.trim(), answers: v.answers.trim() };
      const name = shortName(body.questions, 60);
      if (editing === "edit") {
        await contentApi.faq.edit({ id: v.id, ...body }, dispatch);
        toast.success(faqOnSite({ ...editItem, ...body }).shown ? `“${name}” saved. The Questions page shows the new words.` : `“${name}” saved.`);
      } else {
        const created = await contentApi.faq.add(body, dispatch);
        const id = created?.data?.id;
        if (v.is_main && id) await contentApi.faq.set(id, "is_main", 1, dispatch);
        toast.success(
          v.is_main ? `“${name}” added to the website’s Questions page.` : `“${name}” added. It isn’t on the website until you switch it on.`
        );
      }
      setEditing(null);
      list.reload();
      return true;
    } catch {
      return undefined;
    }
  };

  return (
    <ContentPage
      title="FAQs"
      intro="Questions and answers for the website. The website calls them “Questions” (in its menu and footer) and shows them under “What people ask”."
      where={
        list.items ? (
          <>
            <strong>{plural(counts.live, "FAQ")}</strong> {counts.live === 1 ? "is" : "are"} on the <WhereLink page="faqs" /> now. An FAQ shows
            there when it is switched on and free of filler text.
          </>
        ) : null
      }
      canAdd={can.write}
      addLabel="Add an FAQ"
      onAdd={() => openEditor(null)}
      dialogs={
        <>
          <EditorDialog
            open={!!editing}
            title={editing === "edit" ? "Edit FAQ" : "Add an FAQ"}
            subtitle={editorNote(editing === "edit" ? editItem : null)}
            onClose={() => setEditing(null)}
            onSave={save}
            saveLabel={editing === "edit" ? "Save FAQ" : "Add FAQ"}
            errors={form.errors}
            dirty={form.dirty}
          >
            <Field
              label="Question"
              value={form.values.questions}
              onChange={(value) => form.set("questions", value)}
              error={form.errors.questions}
              hint="As a visitor would ask it, e.g. “How long does a France visa take?”"
              counter={150}
            />
            <Field
              label="Answer"
              value={form.values.answers}
              onChange={(value) => form.set("answers", value)}
              error={form.errors.answers}
              hint="Plain text. Say the answer first, then the details. Only facts we can stand behind."
              multiline
              rows={5}
            />
            <FaqNotes question={form.values.questions} answer={form.values.answers} onFix={(field, value) => form.set(field, value)} />
            <SitePreview where="The website’s Questions page, “What people ask”">
              <FaqPreview question={form.values.questions.trim()} answer={form.values.answers.trim()} />
            </SitePreview>
            {editing === "add" && (
              <HonestyCheck checked={form.values.is_main} onChange={(value) => form.set("is_main", value)}>
                Put it on the website now (leave unticked to check it first)
              </HonestyCheck>
            )}
          </EditorDialog>

          <ConfirmDialog
            open={confirm.open}
            title={`Delete the FAQ “${shortName(confirm.item?.questions, 60)}”?`}
            confirmLabel="Delete"
            cancelLabel="Cancel"
            onClose={confirm.close}
            onConfirm={async () => {
              try {
                await contentApi.faq.remove(confirm.item.id, dispatch);
                // "“Name” deleted." with Undo, as for questions and documents (hala-26).
                const message = `“${shortName(confirm.item?.questions, 60)}” deleted: it is off the website and off every visa it was on.`;
                if (canUndo()) toast.success(<UndoToast message={message} undo={{ table: "country_faq", ids: [confirm.item.id] }} reload={list.reload} />, { autoClose: 20000, closeOnClick: false });
                else toast.success(message);
                list.reload();
                return true;
              } catch {
                return false;
              }
            }}
          >
            It comes off the website, and off any visa it is on. Undo brings it back for 10 seconds; after that, restore it from the Activity
            log. To hide it for now instead, switch it off.
          </ConfirmDialog>
        </>
      }
    >
      <ResourceList
        label="FAQs"
        items={shown}
        primaryLabel="FAQ"
        primary={(item) => ({ title: item.questions, subtitle: shortName(item.answers, 90) })}
        columns={[
          {
            key: "site",
            label: "Live",
            width: 240,
            // One switch, one state per row: "Live", "Off", or
            // "Blocked: …" when it is on but the website still hides it.
            // Blocked, the switch shows what the website does (off), can't
            // be pressed, and the row says how to unblock it: it read ON
            // beside "Blocked: placeholder text" (CMS review round 3, hala-03).
            render: (item) => {
              const site = faqOnSite(item);
              const blocked = site.on && !site.shown;
              return (
                <SaveSwitch
                  label={site.state}
                  item={item.questions}
                  checked={site.shown}
                  disabled={!can.edit || blocked}
                  hint={blocked ? site.unblock : undefined}
                  onChange={(next) => setOnWebsite(item, next)}
                />
              );
            },
          },
          // Wide enough that a warning fits on one line, two at most.
          { key: "check", label: "Needs a look", width: 340, hideBelow: "md", render: (item) => <CheckCell warnings={warningsFor(item)} /> },
        ]}
        onOpen={can.edit ? openEditor : undefined}
        openLabel={(item) => `Edit “${item.questions}”`}
        rowActions={(item) => [
          can.edit && { label: "Edit", onClick: () => openEditor(item) },
          // Its switch can't be pressed while blocked: this keeps it off once the words are fixed.
          can.edit && faqOnSite(item).on && !faqOnSite(item).shown && { label: "Keep it off the website", hint: "It stays off after the words are fixed.", onClick: () => setOnWebsite(item, false) },
          can.delete && { label: "Delete", destructive: true, onClick: () => confirm.ask(item) },
        ]}
        search={{ value: search, onChange: setSearch, placeholder: "Search questions and answers" }}
        filters={
          <ShowFilter
            value={tab}
            onChange={setTab}
            options={[
              { value: "all", label: "All FAQs", count: counts.all },
              { value: "live", label: "Live", count: counts.live },
              { value: "other", label: "Off", count: counts.other },
            ]}
          />
        }
        loading={list.loading}
        error={list.failed}
        onRetry={list.reload}
        empty={{ title: "No FAQs yet.", hint: "Add the first one. It stays off the website until you switch it on." }}
      />
    </ContentPage>
  );
};

export default FAQ;
